/**
 * Role-tagged address parsing (SK-5).
 *
 * ## The defect this exists for
 *
 * `makeTextPairs` zipped the source's address lines against the client's BY
 * INDEX. When the two have different shapes — and they usually do — the result
 * is a plausible-looking location that is wrong:
 *
 *   · `["Memphis","US-TN"]` against `["Arlington","TX","76006"]` rendered
 *     **"Designer in Arlington, TN"**. The city swapped and the region did not,
 *     because the visible text says `TN` while the stored line says `US-TN`.
 *   · `["Message Click To Email Us Location Everett","WA"]` against
 *     `["Miami","FL"]` rendered **"TRUSTED PLUMBERS FOR EVERETT, FL"**. Line 0
 *     was a scraped contact strip that matched nothing, so only the state moved.
 *
 * Neither place exists. Fabricating a location is worse than leaving the
 * template's, because it is not visibly wrong.
 *
 * ## The rule
 *
 * A part is only ever mapped to the SAME ROLE. Where roles cannot be
 * established with confidence the answer is `null` and a report entry — never a
 * positional guess. Positional zipping survives ONLY as the documented
 * backward-compatible fallback for profiles that predate this phase.
 *
 * Field names are `client-info.js`'s canonical set — `lineOne`, `lineTwo`,
 * `city`, `county`, `postcode` — because `client-info-migrate.js` already reads
 * `source.addressParts[fieldKey]` with exactly those names. A second vocabulary
 * beside a live consumer is how the two silently stop meeting.
 *
 * Pure, no imports, Worker-safe.
 */

const EMPTY = Object.freeze({
  lineOne: '',
  lineTwo: '',
  city: '',
  county: '',
  postcode: ''
});

/** US/CA postal shapes, enough to recognise a trailing code with confidence. */
const POSTCODE_RE = /^(?:\d{5}(?:-\d{4})?|[A-Z]\d[A-Z]\s?\d[A-Z]\d)$/i;
const REGION_RE = /^[A-Z]{2}$/;

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Strip an ISO-3166-2 country prefix and report BOTH forms.
 *
 * `<meta name="geo.region" content="US-TN">` is where `US-TN` came from. The
 * page text says `TN`, so a pair built on `US-TN` matches nothing — the city
 * swaps, the state does not, and the mockup claims a city that is not in that
 * state. Emitting both means the scanner still recognises the raw meta form.
 */
export function normalizeRegion(value) {
  const raw = clean(value);
  if (!raw) return { region: '', variants: [] };
  const iso = raw.match(/^[A-Za-z]{2}-([A-Za-z]{2,3})$/);
  if (iso) {
    const region = iso[1].toUpperCase();
    return { region, variants: [region, raw] };
  }
  return { region: raw, variants: [raw] };
}

/**
 * Parse a flat address string into roles.
 *
 * @returns {{parts: object|null, confidence: 'high'|'low', reason: string}}
 *   `parts: null` means the roles could NOT be established. The caller reports
 *   it and leaves the address alone; it must not fall back to guessing.
 */
export function parseAddressParts(value) {
  const raw = clean(value);
  if (!raw) return { parts: null, confidence: 'low', reason: 'no address', regionVariants: [] };

  const segments = raw
    .split(/\n|,/)
    .map((part) => clean(part))
    .filter(Boolean);
  if (!segments.length)
    return { parts: null, confidence: 'low', reason: 'no address', regionVariants: [] };

  const parts = { ...EMPTY };
  let regionVariants = [];
  const rest = [...segments];

  // Work from the END, where the roles are unambiguous: a postcode looks like a
  // postcode and a region is two letters. Anything left over is street.
  const last = rest[rest.length - 1];
  if (last && POSTCODE_RE.test(last)) {
    parts.postcode = rest.pop();
  }
  const nextLast = rest[rest.length - 1];
  if (nextLast) {
    // "TX 76006" — region and postcode in one segment, which is the common
    // single-line form.
    const combined = nextLast.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (combined) {
      rest.pop();
      parts.county = combined[1].toUpperCase();
      if (!parts.postcode) parts.postcode = combined[2];
    } else {
      const region = normalizeRegion(nextLast);
      if (REGION_RE.test(region.region)) {
        rest.pop();
        parts.county = region.region;
        // Keep the RAW form too. `<meta name="geo.region" content="US-TN">` is
        // where `US-TN` came from, and a caller that re-derives variants from
        // the already-normalised `county` loses it — which is how the scanner
        // stops recognising the very string the profile stores.
        regionVariants = region.variants;
      }
    }
  }
  if (rest.length) parts.city = rest.pop();
  if (rest.length) parts.lineOne = rest.shift();
  if (rest.length) parts.lineTwo = rest.join(', ');

  // Confidence is about ROLES, not completeness. A city alone is fine; a single
  // segment we could not classify is not — "Everett" on its own could be a
  // city, a street or a surname, and guessing is what produced "Everett, FL".
  const identified = [parts.postcode, parts.county, parts.city].filter(Boolean).length;
  if (!identified) {
    return {
      parts: null,
      confidence: 'low',
      reason: 'no role could be established',
      regionVariants: []
    };
  }
  if (segments.length === 1 && !parts.postcode && !parts.county) {
    return {
      parts: null,
      confidence: 'low',
      reason: 'a single unclassifiable segment',
      regionVariants: []
    };
  }
  if (!regionVariants.length && parts.county) regionVariants = [parts.county];
  return { parts, confidence: 'high', reason: '', regionVariants };
}

/**
 * Recognise the client-info FIVE-SLOT positional array —
 * `[lineOne, lineTwo, city, county, postcode]` with empty slots PRESERVED —
 * which `clientInfoToSourceProfile` emits and `client-binding-tests.js` pins.
 * It is role-tagged by position, so it can be mapped safely; an arbitrary
 * comma-split of the same length cannot, which is why the empty-slot signature
 * is required rather than just the length.
 */
export function partsFromClientInfoLines(addressLines) {
  if (!Array.isArray(addressLines) || addressLines.length !== 5) return null;
  const hasEmptySlot = addressLines.some((line) => clean(line) === '');
  if (!hasEmptySlot) return null;
  return {
    lineOne: clean(addressLines[0]),
    lineTwo: clean(addressLines[1]),
    city: clean(addressLines[2]),
    county: clean(addressLines[3]),
    postcode: clean(addressLines[4])
  };
}

/** The roles, in the order a reader expects them. */
export const ADDRESS_ROLES = Object.freeze(['lineOne', 'lineTwo', 'city', 'county', 'postcode']);
